import { Form } from "react-router";
import type { Route } from "./+types/ai.credits";
import { Button } from "~/components/ui/button";
import { MarketingLayout } from "~/components/marketing/MarketingLayout";
import { getActorFromRequest } from "~/lib/session.server";
import { detectLocale, translations } from "~/i18n/index";
import type { Locale } from "~/i18n/index";
import { useT } from "~/i18n/context";
import { socialMeta } from "~/lib/seo";
import { STUDIO_CREDIT_COSTS, TOP_UP_PACKS } from "@caratzon/domain";

export async function loader({ request }: Route.LoaderArgs) {
  const locale = detectLocale(
    request.headers.get("Cookie"),
    request.headers.get("Accept-Language"),
  );
  let actor: { authenticated: boolean } = { authenticated: false };
  try {
    const a = await getActorFromRequest(request);
    actor = { authenticated: !!a?.id };
  } catch {}
  return { locale, actor };
}

export function meta({ data }: Route.MetaArgs) {
  const locale = (data?.locale ?? "en") as Locale;
  const t = translations[locale] ?? translations.en;
  const m = t.aiCredits.meta;
  return [
    { title: m.title },
    { name: "description", content: m.description },
    ...socialMeta({ title: m.title, description: m.socialDescription, pathname: "/ai/credits", locale }),
  ];
}

// ── Style tokens ──────────────────────────────────────────────────────────────

const sectionLabel = {
  fontFamily: "var(--font-sans)",
  fontSize: "0.62rem",
  letterSpacing: "0.28em",
  textTransform: "uppercase" as const,
  color: "var(--primary)",
  opacity: 0.8,
  fontWeight: 500,
} as const;

const sectionHeading = {
  fontFamily: "var(--font-display)",
  fontWeight: 300,
  lineHeight: 1.15,
} as const;

const bodyText = {
  fontFamily: "var(--font-sans)",
  fontSize: "0.88rem",
  lineHeight: 1.8,
  color: "var(--muted-foreground)",
} as const;

const smallNote = {
  fontFamily: "var(--font-sans)",
  fontSize: "0.78rem",
  lineHeight: 1.7,
  color: "var(--muted-foreground)",
} as const;

// ── Component ─────────────────────────────────────────────────────────────────

export default function CreditsPage({ loaderData }: Route.ComponentProps) {
  const { actor } = loaderData;
  const { t } = useT();
  const c = t.aiCredits;

  const photoStudioActions = c.actions.photoStudio.map((a, i) => ({
    action: a.action,
    credits: [STUDIO_CREDIT_COSTS.essentialsKit, STUDIO_CREDIT_COSTS.caratzonKit, STUDIO_CREDIT_COSTS.singlePhotoRegen, STUDIO_CREDIT_COSTS.lookbookPdf][i],
    detail: a.detail,
  }));

  const caraAiActions = c.actions.caraAi.map((a, i) => ({
    action: a.action,
    credits: [STUDIO_CREDIT_COSTS.caraMessage, STUDIO_CREDIT_COSTS.caraSketch, STUDIO_CREDIT_COSTS.caraImage, STUDIO_CREDIT_COSTS.caraLookbook, STUDIO_CREDIT_COSTS.caraVideo][i],
    detail: a.detail,
  }));

  return (
    <MarketingLayout>
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        {/* ── Hero ── */}
        <div className="text-center mb-20">
          <span style={sectionLabel}>{c.hero.label}</span>
          <h1
            className="text-foreground mt-3"
            style={{ ...sectionHeading, fontSize: "clamp(2rem, 4vw, 3rem)" }}
          >
            {c.hero.heading}
          </h1>
          <p
            className="mt-5 mx-auto leading-relaxed"
            style={{ ...bodyText, fontSize: "1rem", maxWidth: "48ch" }}
          >
            {c.hero.body}
          </p>
        </div>

        {/* ── How It Works ── */}
        <section className="mb-20">
          <div className="rule mb-10" />
          <span style={sectionLabel}>{c.howItWorks.label}</span>
          <h2
            className="text-foreground mt-3 mb-6"
            style={{ ...sectionHeading, fontSize: "clamp(1.5rem, 3vw, 2rem)" }}
          >
            {c.howItWorks.heading}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {c.howItWorks.steps.map((s) => (
              <div key={s.step}>
                <span
                  className="block mb-2"
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: "2rem",
                    fontWeight: 300,
                    color: "var(--teal-sapphire)",
                    opacity: 0.5,
                  }}
                >
                  {s.step}
                </span>
                <h3
                  className="text-foreground mb-2"
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontSize: "0.88rem",
                    fontWeight: 600,
                  }}
                >
                  {s.title}
                </h3>
                <p style={smallNote}>{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Photo Studio Actions ── */}
        <section className="mb-20">
          <div className="rule mb-10" />
          <span style={sectionLabel}>{c.photoStudio.label}</span>
          <h2
            className="text-foreground mt-3 mb-3"
            style={{ ...sectionHeading, fontSize: "clamp(1.5rem, 3vw, 2rem)" }}
          >
            {c.photoStudio.heading}
          </h2>
          <p className="mb-8" style={bodyText}>
            {c.photoStudio.body}
          </p>
          <CreditGrid items={photoStudioActions} creditsLabel={c.topUpPacks.creditsLabel} />
        </section>

        {/* ── Cara AI Actions ── */}
        <section className="mb-20">
          <div className="rule mb-10" />
          <span style={sectionLabel}>{c.caraAi.label}</span>
          <h2
            className="text-foreground mt-3 mb-3"
            style={{ ...sectionHeading, fontSize: "clamp(1.5rem, 3vw, 2rem)" }}
          >
            {c.caraAi.heading}
          </h2>
          <p className="mb-8" style={bodyText}>
            {c.caraAi.body}
          </p>
          <CreditGrid items={caraAiActions} creditsLabel={c.topUpPacks.creditsLabel} />
          <p className="mt-6" style={smallNote}>
            {c.caraAi.note}
          </p>
        </section>

        {/* ── Monthly Allowances ── */}
        <section className="mb-20">
          <div className="rule mb-10" />
          <span style={sectionLabel}>{c.monthlyAllowances.label}</span>
          <h2
            className="text-foreground mt-3 mb-8"
            style={{ ...sectionHeading, fontSize: "clamp(1.5rem, 3vw, 2rem)" }}
          >
            {c.monthlyAllowances.heading}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-left" style={{ fontFamily: "var(--font-sans)" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border)" }}>
                  {c.monthlyAllowances.tableHeaders.map((h) => (
                    <th
                      key={h}
                      className="pb-3"
                      style={{
                        fontSize: "0.65rem",
                        letterSpacing: "0.14em",
                        textTransform: "uppercase",
                        color: "var(--muted-foreground)",
                        fontWeight: 500,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {c.monthlyAllowances.rows.map((row) => (
                  <tr
                    key={row.plan}
                    style={{ borderBottom: "1px solid var(--border)" }}
                  >
                    <td className="py-3" style={{ fontSize: "0.85rem", fontWeight: 500 }}>
                      {row.plan}
                    </td>
                    <td className="py-3" style={{ fontSize: "0.85rem", color: "var(--muted-foreground)" }}>
                      {row.monthly}
                    </td>
                    <td className="py-3" style={{ fontSize: "0.85rem", color: "var(--muted-foreground)" }}>
                      {row.welcome}
                    </td>
                    <td className="py-3" style={{ fontSize: "0.85rem", color: "var(--muted-foreground)" }}>
                      {row.price}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-5" style={smallNote}>
            {c.monthlyAllowances.note}
          </p>
        </section>

        {/* ── Top-Up Packs ── */}
        <section className="mb-20">
          <div className="rule mb-10" />
          <span style={sectionLabel}>{c.topUpPacks.label}</span>
          <h2
            className="text-foreground mt-3 mb-3"
            style={{ ...sectionHeading, fontSize: "clamp(1.5rem, 3vw, 2rem)" }}
          >
            {c.topUpPacks.heading}
          </h2>
          <p className="mb-8" style={bodyText}>
            {c.topUpPacks.body}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {(() => {
              const packs = Object.values(TOP_UP_PACKS);
              const costs = packs.map((p) => p.priceUsd / p.credits);
              const bestIdx = costs.indexOf(Math.min(...costs));
              return packs.map((pack, i) => {
                const centsPerCredit = ((pack.priceUsd / pack.credits) * 100).toFixed(1);
                const isBest = i === bestIdx;
                return (
                  <div
                    key={pack.id}
                    className={`rounded-xl border bg-card p-8 text-center transition-shadow ${
                      isBest
                        ? "border-border ring-1 shadow-[0_2px_24px_rgba(0,0,0,0.06)] relative"
                        : "border-border hover:shadow-[0_2px_16px_rgba(0,0,0,0.04)]"
                    }`}
                    style={isBest ? { borderColor: "oklch(0.45 0.08 213 / 0.3)" } : undefined}
                  >
                    {isBest && (
                      <div className="flex justify-center -mt-12 mb-4">
                        <span
                          className="inline-block px-4 py-1 rounded-full text-white font-medium"
                          style={{
                            fontSize: "0.6rem",
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                            background: "var(--teal-sapphire)",
                          }}
                        >
                          {c.topUpPacks.bestValue}
                        </span>
                      </div>
                    )}
                    <span
                      className="font-display font-light block"
                      style={{ fontSize: "2.4rem", lineHeight: 1 }}
                    >
                      {pack.credits.toLocaleString()}
                    </span>
                    <span
                      className="text-muted-foreground block mt-1"
                      style={{
                        fontSize: "0.7rem",
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                      }}
                    >
                      {c.topUpPacks.creditsLabel}
                    </span>
                    <div className="rule my-5" />
                    <span
                      className="font-display font-light block"
                      style={{ fontSize: "1.6rem", lineHeight: 1 }}
                    >
                      ${pack.priceUsd}
                    </span>
                    <span
                      className="text-muted-foreground block mt-1.5"
                      style={{ fontSize: "0.75rem" }}
                    >
                      ${centsPerCredit}{c.topUpPacks.perCredit}
                    </span>
                    {actor.authenticated && (
                      <Form
                        method="post"
                        action="/resources/credit-top-up"
                        className="mt-5"
                      >
                        <input type="hidden" name="packId" value={pack.id} />
                        <Button variant="outline" className="w-full" type="submit">
                          {c.topUpPacks.buyPrefix} {pack.credits.toLocaleString()} {c.topUpPacks.buySuffix}
                        </Button>
                      </Form>
                    )}
                  </div>
                );
              });
            })()}
          </div>

          <p className="text-center mt-8" style={smallNote}>
            {c.topUpPacks.note}{" "}
            <a
              href="/brands/pricing"
              className="underline underline-offset-2 hover:text-foreground transition-colors"
            >
              {c.topUpPacks.compareLink}
            </a>
          </p>
        </section>

        {/* ── FAQ ── */}
        <section>
          <div className="rule mb-10" />
          <span style={sectionLabel}>{c.faq.label}</span>
          <h2
            className="text-foreground mt-3 mb-8"
            style={{ ...sectionHeading, fontSize: "clamp(1.5rem, 3vw, 2rem)" }}
          >
            {c.faq.heading}
          </h2>
          <div className="space-y-8">
            {c.faq.items.map((faq) => (
              <div key={faq.q}>
                <h3
                  className="text-foreground mb-2"
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontSize: "0.88rem",
                    fontWeight: 600,
                  }}
                >
                  {faq.q}
                </h3>
                <p style={smallNote}>{faq.a}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
}

// ── Shared credit grid ────────────────────────────────────────────────────────

function CreditGrid({
  items,
  creditsLabel,
}: {
  items: { action: string; credits: number; detail: string }[];
  creditsLabel: string;
}) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
      {items.map((item) => (
        <div
          key={item.action}
          className="rounded-xl border border-border bg-card p-6 text-center"
        >
          <span
            className="font-display font-light block"
            style={{
              fontSize: "2rem",
              lineHeight: 1,
              color: "var(--teal-sapphire)",
            }}
          >
            {item.credits}
          </span>
          <span
            className="text-muted-foreground block mt-1"
            style={{
              fontSize: "0.7rem",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            {creditsLabel}
          </span>
          <span
            className="text-foreground font-medium block mt-3"
            style={{ fontSize: "0.88rem" }}
          >
            {item.action}
          </span>
          <span
            className="text-muted-foreground block mt-1 leading-snug"
            style={{ fontSize: "0.78rem" }}
          >
            {item.detail}
          </span>
        </div>
      ))}
    </div>
  );
}
